import { useState, useCallback } from 'react';
import { camelCaseMapData } from '@utils/formater';
import rateSvc from '../../../../../../../services/rate/rateSvc';
import {
  ExchangeResponse,
  IExchange,
} from '../../../../../../../services/rate/types';
import { getExchangeResult } from '../../../../../../../utils/convertion';
import {
  validatePromoterRate,
  getBaseRateForValidation,
} from '../../../../utils/promoterRateHelpers';
import {
  UsePromoterRateState,
  UsePromoterRateReturn,
  PromoterRateParams,
  ExchangeParams,
} from './usePromoterRate.types';

export const usePromoterRate = (): UsePromoterRateReturn => {
  const [state, setState] = useState<UsePromoterRateState>({
    createLoading: false,
    createData: null,
    createError: null,
    exchangeLoading: false,
    activeRateUuid: null,
  });

  const recalculateWithCurrentRate = useCallback(
    async (params: ExchangeParams) => {
      const { currency, desCurrency, enterpriseId } = params;

      if (!currency || !desCurrency) {
        return getExchangeResult({
          ...params,
          setCancelFunction: params.setCancelFunction || (() => {}),
        });
      }

      try {
        const currentRateResponse = await rateSvc.getExchange({
          currency,
          desCurrency,
          enterpriseId,
        });

        const currentRate = camelCaseMapData<ExchangeResponse, IExchange>(
          currentRateResponse
        );

        if (currentRate.reference) {
          return await getExchangeResult({
            ...params,
            rateUuid: currentRate.reference,
            setCancelFunction: params.setCancelFunction || (() => {}),
          });
        }
      } catch (error) {
        throw new Error(
          `Failed to fetch current exchange rate in recalculateWithCurrentRate: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }

      return getExchangeResult({
        ...params,
        setCancelFunction: params.setCancelFunction || (() => {}),
      });
    },
    []
  );

  const createRateAndCalculate = useCallback(
    async (params: PromoterRateParams) => {
      const {
        promoterId,
        originalReference,
        promoterRate,
        currentAmount,
        isReverse,
        enterpriseId,
        onCalculationResult,
        setCancelFunction,
      } = params;

      if (
        !promoterId ||
        !originalReference ||
        !promoterRate ||
        !currentAmount
      ) {
        return;
      }

      setState((prev) => ({
        ...prev,
        createLoading: true,
        createError: null,
        exchangeLoading: true,
      }));

      try {
        const response = await rateSvc.createPromoterRate({
          promoterId,
          reference: originalReference,
          promoterRate,
        });

        const { newRateUuid } = response?.data || {};

        if (!newRateUuid) {
          throw new Error('Invalid response: newRateUuid missing');
        }

        setState((prev) => ({
          ...prev,
          createLoading: false,
          createData: response.data,
          activeRateUuid: newRateUuid,
        }));

        getExchangeResult({
          amount: currentAmount,
          rateUuid: newRateUuid,
          reverse: isReverse,
          enterpriseId,
          requestFee: true,
          onChangeResult: ({ amount: result, feeDescription }) => {
            setState((prev) => ({
              ...prev,
              exchangeLoading: false,
            }));

            onCalculationResult({
              calculatedAmount: result,
              feeDescription,
              activeRateUuid: newRateUuid,
            });
          },
          setCancelFunction: setCancelFunction || (() => {}),
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to create promoter rate';

        setState((prev) => ({
          ...prev,
          createLoading: false,
          exchangeLoading: false,
          createError: errorMessage,
        }));
      }
    },
    []
  );

  const createRate = useCallback(
    async (params: {
      promoterId: string;
      reference: string;
      promoterRate: string;
    }) => {
      setState((prev) => ({
        ...prev,
        createLoading: true,
        createError: null,
      }));

      try {
        const response = await rateSvc.createPromoterRate(params);

        const { newRateUuid } = response?.data || {};

        setState((prev) => ({
          ...prev,
          createLoading: false,
          createData: response.data,
          activeRateUuid: newRateUuid || prev.activeRateUuid,
        }));

        return response.data;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred';
        setState((prev) => ({
          ...prev,
          createLoading: false,
          createError: errorMessage,
        }));
        return null;
      }
    },
    []
  );

  const clearActivePromoterRate = useCallback(() => {
    setState((prev) => ({
      ...prev,
      activeRateUuid: null,
      createData: null,
    }));
  }, []);

  return {
    ...state,
    createRateAndCalculate,
    createRate,
    recalculateWithCurrentRate,
    validatePromoterRate,
    getBaseRateForValidation,
    clearActivePromoterRate,
  };
};

export default usePromoterRate;
